/**
 * Room Drawing Utilities
 *
 * Canvas drawing functions for the polygon-in-progress overlay
 * during room creation mode. All functions operate on a 2D canvas
 * context in world coordinates (already transformed).
 */

import { NO_SNAP } from './vertexSnap';

// Touch users don't get a hover indicator — we need to show the close-target
// ring permanently once the polygon is closeable so they know what to tap.
const IS_TOUCH_PRIMARY =
  typeof window !== 'undefined' &&
  ('ontouchstart' in window || navigator.maxTouchPoints > 0);

const VERTEX_RADIUS = 0.08; // meters
const VERTEX_COLOR = '#2196F3';
const EDGE_COLOR = '#2196F3';
const PREVIEW_EDGE_COLOR = '#90CAF9';
const CLOSE_INDICATOR_COLOR = '#4CAF50';
const SNAP_RING_COLOR = '#FF9800';
const SNAP_RING_RADIUS = 0.12;
const DELETE_VERTEX_COLOR = '#EF4444';
const DELETE_VERTEX_RADIUS = 0.12;
const ADD_VERTEX_COLOR = '#4CAF50';
const ADD_VERTEX_RADIUS = 0.1;
const HOVERED_EDGE_COLOR = '#4CAF50';
const MOVE_VERTEX_COLOR = '#F59E0B';
const MOVE_VERTEX_RADIUS = 0.12;
// Magenta — distinct from all other edge/vertex colors (blue/amber/green/red)
const SNAP_ENGAGED_COLOR = '#E91E63';

/**
 * Draw the room polygon preview overlay.
 * @param {CanvasRenderingContext2D} ctx - Canvas context (already in world-space transform)
 * @param {Array<{x: number, z: number}>} vertices - Placed vertices
 * @param {{x: number, z: number}|null} cursorPos - Current cursor world position
 * @param {{type: string, position: {x: number, z: number}}|null} snapInfo - Snap target info
 * @param {boolean} isNearFirstVertex - Whether cursor is near first vertex (can close)
 * @param {number} scale - Current canvas scale (for line width calculation)
 */
export function drawRoomPolygonPreview(
  ctx,
  vertices,
  cursorPos,
  snapInfo,
  isNearFirstVertex,
  scale
) {
  if (!vertices || vertices.length === 0) return;

  const lineWidth = 2 / scale;
  const dashSize = 0.1;

  ctx.save();

  // Inference guide lines (drawn first, under everything else).
  if (snapInfo?.guides?.length) {
    drawSnapGuides(ctx, snapInfo.guides, scale);
  }

  // Draw placed edges (solid lines)
  if (vertices.length >= 2) {
    ctx.strokeStyle = EDGE_COLOR;
    ctx.lineWidth = lineWidth;
    ctx.setLineDash([]);
    ctx.beginPath();
    ctx.moveTo(vertices[0].x, vertices[0].z);
    for (let i = 1; i < vertices.length; i++) {
      ctx.lineTo(vertices[i].x, vertices[i].z);
    }
    ctx.stroke();
  }

  // Draw semi-transparent fill for the polygon area
  if (vertices.length >= 3) {
    ctx.fillStyle = 'rgba(33, 150, 243, 0.08)';
    ctx.beginPath();
    ctx.moveTo(vertices[0].x, vertices[0].z);
    for (let i = 1; i < vertices.length; i++) {
      ctx.lineTo(vertices[i].x, vertices[i].z);
    }
    if (cursorPos) {
      ctx.lineTo(cursorPos.x, cursorPos.z);
    }
    ctx.closePath();
    ctx.fill();
  }

  // Draw dashed preview line from last vertex to cursor
  if (cursorPos && vertices.length > 0) {
    const last = vertices[vertices.length - 1];
    ctx.strokeStyle = PREVIEW_EDGE_COLOR;
    ctx.lineWidth = lineWidth;
    ctx.setLineDash([dashSize, dashSize]);
    ctx.beginPath();
    ctx.moveTo(last.x, last.z);
    ctx.lineTo(cursorPos.x, cursorPos.z);
    ctx.stroke();
    ctx.setLineDash([]);

    // If near first vertex and closeable, draw closing edge preview
    if (isNearFirstVertex && vertices.length >= 3) {
      ctx.strokeStyle = CLOSE_INDICATOR_COLOR;
      ctx.lineWidth = lineWidth;
      ctx.setLineDash([dashSize, dashSize]);
      ctx.beginPath();
      ctx.moveTo(cursorPos.x, cursorPos.z);
      ctx.lineTo(vertices[0].x, vertices[0].z);
      ctx.stroke();
      ctx.setLineDash([]);
    }
  }

  // Draw placed vertices as dots
  for (let i = 0; i < vertices.length; i++) {
    const v = vertices[i];
    const isFirst = i === 0;
    // Touch users don't get a hover signal — once the polygon is closeable,
    // show the close-target ring permanently so they discover it.
    const showCloseTarget =
      isFirst &&
      vertices.length >= 3 &&
      (isNearFirstVertex || IS_TOUCH_PRIMARY);

    ctx.beginPath();
    ctx.arc(v.x, v.z, VERTEX_RADIUS, 0, Math.PI * 2);

    if (showCloseTarget) {
      // Reserve the strong green fill for when the cursor/finger is actually
      // within range. The permanent touch ring uses the regular blue dot
      // underneath so users can still tell their finger is off-target.
      if (isNearFirstVertex) {
        ctx.fillStyle = CLOSE_INDICATOR_COLOR;
      } else {
        ctx.fillStyle = VERTEX_COLOR;
      }
      ctx.strokeStyle = '#FFFFFF';
      ctx.lineWidth = lineWidth * 0.8;
      ctx.fill();
      ctx.stroke();

      // Draw a larger ring around first vertex as close target
      ctx.beginPath();
      ctx.arc(v.x, v.z, VERTEX_RADIUS * 2.5, 0, Math.PI * 2);
      ctx.strokeStyle = CLOSE_INDICATOR_COLOR;
      ctx.lineWidth = lineWidth * 0.6;
      ctx.setLineDash([dashSize * 0.5, dashSize * 0.5]);
      ctx.stroke();
      ctx.setLineDash([]);
    } else {
      ctx.fillStyle = VERTEX_COLOR;
      ctx.fill();
      // White border for contrast
      ctx.strokeStyle = '#FFFFFF';
      ctx.lineWidth = lineWidth * 0.6;
      ctx.stroke();
    }
  }

  ctx.restore();
}

/**
 * Draw inference guide lines (collinear / axis / parallel-perp) for the
 * current snap. Each guide is a world-space segment {from, to}.
 * @param {CanvasRenderingContext2D} ctx
 * @param {Array<{from:{x:number,z:number}, to:{x:number,z:number}, subtype:string}>} guides
 * @param {number} scale
 */
export function drawSnapGuides(ctx, guides, scale) {
  if (!guides || guides.length === 0) return;
  ctx.save();
  ctx.strokeStyle = SNAP_RING_COLOR;
  ctx.lineWidth = 1 / scale;
  ctx.setLineDash([0.08, 0.06]);
  for (const g of guides) {
    ctx.beginPath();
    ctx.moveTo(g.from.x, g.from.z);
    ctx.lineTo(g.to.x, g.to.z);
    ctx.stroke();
  }
  ctx.setLineDash([]);
  ctx.restore();
}

/**
 * Draw a snap indicator at the given position.
 * @param {CanvasRenderingContext2D} ctx
 * @param {{x: number, z: number}} point
 * @param {string} type - 'roomCorner', 'wallCorner', 'angle', 'intersection',
 *   'line', or 'free'
 * @param {number} scale
 * @param {{prev?, next?, prevSnapped?, nextSnapped?}} [target] - extra payload
 *   for 'angle' snap (anchor vertices) used to draw the alignment guide lines.
 */
export function drawSnapIndicator(ctx, point, type, scale, target = null) {
  if (!point || type === 'free') return;

  const lineWidth = 1.5 / scale;
  const s = SNAP_RING_RADIUS;

  ctx.save();
  ctx.strokeStyle = SNAP_RING_COLOR;
  ctx.lineWidth = lineWidth;

  if (type === 'roomCorner') {
    // Diamond shape for an existing room-corner snap.
    ctx.beginPath();
    ctx.moveTo(point.x, point.z - s);
    ctx.lineTo(point.x + s, point.z);
    ctx.lineTo(point.x, point.z + s);
    ctx.lineTo(point.x - s, point.z);
    ctx.closePath();
    ctx.stroke();
  } else if (type === 'wallCorner') {
    // Circle for a wall-corner snap.
    ctx.beginPath();
    ctx.arc(point.x, point.z, SNAP_RING_RADIUS, 0, Math.PI * 2);
    ctx.stroke();
  } else {
    // Plus-sign cross for angle / line / intersection snaps. For 'angle',
    // also draw dashed guide line(s) back to each contributing anchor; the
    // collinear/axis guides for 'line'/'intersection' are drawn separately
    // via drawSnapGuides from snapInfo.guides.
    if (type === 'angle' && target) {
      ctx.setLineDash([0.06, 0.04]);
      if (target.prevSnapped && target.prev) {
        ctx.beginPath();
        ctx.moveTo(target.prev.x, target.prev.z);
        ctx.lineTo(point.x, point.z);
        ctx.stroke();
      }
      if (target.nextSnapped && target.next) {
        ctx.beginPath();
        ctx.moveTo(target.next.x, target.next.z);
        ctx.lineTo(point.x, point.z);
        ctx.stroke();
      }
      ctx.setLineDash([]);
    }
    ctx.beginPath();
    ctx.moveTo(point.x - s, point.z);
    ctx.lineTo(point.x + s, point.z);
    ctx.moveTo(point.x, point.z - s);
    ctx.lineTo(point.x, point.z + s);
    ctx.stroke();
  }

  ctx.restore();
}

/**
 * Draw vertex-delete overlay on a room polygon.
 * Shows all vertices as blue dots, with the hovered vertex as a red dot with ring.
 * @param {CanvasRenderingContext2D} ctx - Canvas context (already in world-space transform)
 * @param {Array<{x: number, z: number}>} vertices - Room polygon vertices
 * @param {number|null} hoveredVertexIndex - Index of vertex under cursor
 * @param {number} scale - Current canvas scale
 */
export function drawVertexDeleteOverlay(
  ctx,
  vertices,
  hoveredVertexIndex,
  scale
) {
  if (!vertices || vertices.length === 0) return;

  const lineWidth = 1.5 / scale;

  ctx.save();

  // Draw polygon edges as thin lines for context
  ctx.strokeStyle = EDGE_COLOR;
  ctx.lineWidth = lineWidth;
  ctx.setLineDash([]);
  ctx.beginPath();
  ctx.moveTo(vertices[0].x, vertices[0].z);
  for (let i = 1; i < vertices.length; i++) {
    ctx.lineTo(vertices[i].x, vertices[i].z);
  }
  ctx.closePath();
  ctx.stroke();

  // Draw all vertices
  for (let i = 0; i < vertices.length; i++) {
    const v = vertices[i];
    const isHovered = i === hoveredVertexIndex;

    if (isHovered) {
      // Hovered vertex — larger red circle with ring
      ctx.beginPath();
      ctx.arc(v.x, v.z, DELETE_VERTEX_RADIUS, 0, Math.PI * 2);
      ctx.fillStyle = DELETE_VERTEX_COLOR;
      ctx.fill();
      ctx.strokeStyle = '#FFFFFF';
      ctx.lineWidth = lineWidth * 0.8;
      ctx.stroke();

      // Outer red ring
      ctx.beginPath();
      ctx.arc(v.x, v.z, DELETE_VERTEX_RADIUS * 2, 0, Math.PI * 2);
      ctx.strokeStyle = DELETE_VERTEX_COLOR;
      ctx.lineWidth = lineWidth * 0.6;
      ctx.stroke();
    } else {
      // Normal vertex — blue dot
      ctx.beginPath();
      ctx.arc(v.x, v.z, VERTEX_RADIUS, 0, Math.PI * 2);
      ctx.fillStyle = VERTEX_COLOR;
      ctx.fill();
      ctx.strokeStyle = '#FFFFFF';
      ctx.lineWidth = lineWidth * 0.6;
      ctx.stroke();
    }
  }

  ctx.restore();
}

/**
 * Draw vertex-add overlay on a room polygon.
 *
 * Phase 1 (edge selection): Shows all edges, highlights the hovered edge in green,
 *   draws existing vertices as blue dots, and the preview insertion point as a green
 *   dot with dashed ring.
 * Phase 2 (placement): Shows the polygon with the inserted vertex at previewPosition,
 *   highlights the two new edges from the split in green, and draws the vertex as
 *   a green dot following the cursor.
 *
 * @param {CanvasRenderingContext2D} ctx - Canvas context (already in world-space transform)
 * @param {Array<{x: number, z: number}>} vertices - Room polygon vertices (includes inserted vertex in phase 2)
 * @param {number|null} hoveredEdgeIndex - Index of edge under cursor (phase 1 only)
 * @param {{x: number, z: number}|null} previewPoint - Point where vertex would be inserted (phase 1 only)
 * @param {number} scale - Current canvas scale
 * @param {number|null} insertedVertexIndex - Index of inserted vertex (null = phase 1, number = phase 2)
 * @param {{x: number, z: number}|null} previewPosition - Cursor position for placed vertex (phase 2 only)
 * @param {{type: string, prevSnapped: boolean, nextSnapped: boolean}} [snapInfo]
 *   Snap state from `useVertexAdd`. When a neighbor is snap-locked, the
 *   corresponding split edge renders in SNAP_ENGAGED_COLOR. Defaults to NO_SNAP.
 */
export function drawVertexAddOverlay(
  ctx,
  vertices,
  hoveredEdgeIndex,
  previewPoint,
  scale,
  insertedVertexIndex,
  previewPosition,
  snapInfo = NO_SNAP
) {
  if (!vertices || vertices.length === 0) return;

  const lineWidth = 1.5 / scale;
  const dashSize = 0.08;

  ctx.save();

  if (insertedVertexIndex !== null && previewPosition) {
    // ---- Phase 2: Placement preview ----

    // Draw original polygon edges as faded blue
    ctx.strokeStyle = 'rgba(33, 150, 243, 0.3)';
    ctx.lineWidth = lineWidth;
    ctx.setLineDash([dashSize, dashSize]);
    ctx.beginPath();
    ctx.moveTo(vertices[0].x, vertices[0].z);
    for (let i = 1; i < vertices.length; i++) {
      ctx.lineTo(vertices[i].x, vertices[i].z);
    }
    ctx.closePath();
    ctx.stroke();
    ctx.setLineDash([]);

    // Draw preview polygon with moved vertex
    const n = vertices.length;
    const prevIdx = (insertedVertexIndex - 1 + n) % n;
    const nextIdx = (insertedVertexIndex + 1) % n;

    for (let i = 0; i < n; i++) {
      const posA =
        i === insertedVertexIndex
          ? previewPosition
          : { x: vertices[i].x, z: vertices[i].z };
      const j = (i + 1) % n;
      const posB =
        j === insertedVertexIndex
          ? previewPosition
          : { x: vertices[j].x, z: vertices[j].z };

      // The two split edges: edge from prev-neighbor to inserted (i === prevIdx)
      // and edge from inserted to next-neighbor (i === insertedVertexIndex).
      // Snap-lock is per-neighbor, so map each split edge to its neighbor.
      const isPrevEdge = i === prevIdx;
      const isNextEdge = i === insertedVertexIndex;
      const isSplitEdge = isPrevEdge || isNextEdge;
      const isSnapped =
        (isPrevEdge && snapInfo.prevSnapped) ||
        (isNextEdge && snapInfo.nextSnapped);

      ctx.beginPath();
      ctx.moveTo(posA.x, posA.z);
      ctx.lineTo(posB.x, posB.z);
      if (isSnapped) {
        ctx.strokeStyle = SNAP_ENGAGED_COLOR;
        ctx.lineWidth = lineWidth * 2.5;
        ctx.setLineDash([]);
      } else if (isSplitEdge) {
        ctx.strokeStyle = ADD_VERTEX_COLOR;
        ctx.lineWidth = lineWidth * 2;
        ctx.setLineDash([]);
      } else {
        ctx.strokeStyle = EDGE_COLOR;
        ctx.lineWidth = lineWidth * 1.5;
        ctx.setLineDash([dashSize, dashSize]);
      }
      ctx.stroke();
    }
    ctx.setLineDash([]);

    // Draw all non-inserted vertices as blue dots
    for (let i = 0; i < n; i++) {
      if (i === insertedVertexIndex) continue;
      const v = vertices[i];
      ctx.beginPath();
      ctx.arc(v.x, v.z, VERTEX_RADIUS, 0, Math.PI * 2);
      ctx.fillStyle = VERTEX_COLOR;
      ctx.fill();
      ctx.strokeStyle = '#FFFFFF';
      ctx.lineWidth = lineWidth * 0.6;
      ctx.stroke();
    }

    // Draw inserted vertex at preview position as green dot
    ctx.beginPath();
    ctx.arc(
      previewPosition.x,
      previewPosition.z,
      MOVE_VERTEX_RADIUS,
      0,
      Math.PI * 2
    );
    ctx.fillStyle = ADD_VERTEX_COLOR;
    ctx.fill();
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = lineWidth * 0.8;
    ctx.stroke();
  } else {
    // ---- Phase 1: Edge selection ----

    // Draw all polygon edges, highlighting the hovered one
    for (let i = 0; i < vertices.length; i++) {
      const a = vertices[i];
      const b = vertices[(i + 1) % vertices.length];
      const isHovered = i === hoveredEdgeIndex;

      ctx.beginPath();
      ctx.moveTo(a.x, a.z);
      ctx.lineTo(b.x, b.z);
      ctx.strokeStyle = isHovered ? HOVERED_EDGE_COLOR : EDGE_COLOR;
      ctx.lineWidth = isHovered ? lineWidth * 2 : lineWidth;
      ctx.setLineDash([]);
      ctx.stroke();
    }

    // Draw all existing vertices as blue dots
    for (let i = 0; i < vertices.length; i++) {
      const v = vertices[i];
      ctx.beginPath();
      ctx.arc(v.x, v.z, VERTEX_RADIUS, 0, Math.PI * 2);
      ctx.fillStyle = VERTEX_COLOR;
      ctx.fill();
      ctx.strokeStyle = '#FFFFFF';
      ctx.lineWidth = lineWidth * 0.6;
      ctx.stroke();
    }

    // Draw preview point as green dot with dashed ring
    if (previewPoint && hoveredEdgeIndex !== null) {
      // Filled green dot
      ctx.beginPath();
      ctx.arc(
        previewPoint.x,
        previewPoint.z,
        ADD_VERTEX_RADIUS,
        0,
        Math.PI * 2
      );
      ctx.fillStyle = ADD_VERTEX_COLOR;
      ctx.fill();
      ctx.strokeStyle = '#FFFFFF';
      ctx.lineWidth = lineWidth * 0.8;
      ctx.stroke();

      // Dashed outer ring
      ctx.beginPath();
      ctx.arc(
        previewPoint.x,
        previewPoint.z,
        ADD_VERTEX_RADIUS * 2,
        0,
        Math.PI * 2
      );
      ctx.strokeStyle = ADD_VERTEX_COLOR;
      ctx.lineWidth = lineWidth * 0.6;
      ctx.setLineDash([dashSize, dashSize]);
      ctx.stroke();
      ctx.setLineDash([]);
    }
  }

  ctx.restore();
}

/**
 * Draw vertex-move overlay on a room polygon.
 * Shows all vertices, highlights hovered/selected vertex in amber,
 * and draws a preview polygon when a vertex is being moved.
 * @param {CanvasRenderingContext2D} ctx - Canvas context (already in world-space transform)
 * @param {Array<{x: number, z: number}>} vertices - Room polygon vertices
 * @param {number|null} hoveredVertexIndex - Index of vertex under cursor (when nothing selected)
 * @param {number|null} selectedVertexIndex - Index of vertex currently being moved
 * @param {{x: number, z: number}|null} previewPosition - Where the selected vertex would land
 * @param {number} scale - Current canvas scale
 * @param {{type: string, prevSnapped: boolean, nextSnapped: boolean}} [snapInfo]
 *   Snap state from `useVertexMove`. When a neighbor is snap-locked, the
 *   corresponding adjacent edge renders in SNAP_ENGAGED_COLOR. Defaults to NO_SNAP.
 */
export function drawVertexMoveOverlay(
  ctx,
  vertices,
  hoveredVertexIndex,
  selectedVertexIndex,
  previewPosition,
  scale,
  snapInfo = NO_SNAP
) {
  if (!vertices || vertices.length === 0) return;

  const lineWidth = 1.5 / scale;
  const dashSize = 0.08;

  ctx.save();

  if (selectedVertexIndex !== null && previewPosition) {
    // A vertex is picked up — draw preview polygon with moved vertex

    // Draw original polygon edges as faded blue
    ctx.strokeStyle = 'rgba(33, 150, 243, 0.3)';
    ctx.lineWidth = lineWidth;
    ctx.setLineDash([dashSize, dashSize]);
    ctx.beginPath();
    ctx.moveTo(vertices[0].x, vertices[0].z);
    for (let i = 1; i < vertices.length; i++) {
      ctx.lineTo(vertices[i].x, vertices[i].z);
    }
    ctx.closePath();
    ctx.stroke();
    ctx.setLineDash([]);

    // Draw preview polygon with moved vertex.
    // Fast path (no snap): single continuous path so dashes wrap cleanly
    // around the polygon perimeter, matching the pre-snap-feature behavior.
    // Snap path: per-edge loop so the adjacent-and-snap-locked edges can
    // render in the snap-engaged color and thickness.
    const n = vertices.length;
    const anySnapped = snapInfo.prevSnapped || snapInfo.nextSnapped;

    if (!anySnapped) {
      ctx.strokeStyle = MOVE_VERTEX_COLOR;
      ctx.lineWidth = lineWidth * 1.5;
      ctx.setLineDash([dashSize, dashSize]);
      ctx.beginPath();
      for (let i = 0; i < n; i++) {
        const pos =
          i === selectedVertexIndex
            ? previewPosition
            : { x: vertices[i].x, z: vertices[i].z };
        if (i === 0) {
          ctx.moveTo(pos.x, pos.z);
        } else {
          ctx.lineTo(pos.x, pos.z);
        }
      }
      ctx.closePath();
      ctx.stroke();
      ctx.setLineDash([]);
    } else {
      const prevEdgeIdx = (selectedVertexIndex - 1 + n) % n; // edge from prev to selected
      const selectedEdgeIdx = selectedVertexIndex; // edge from selected to next

      for (let i = 0; i < n; i++) {
        const posA =
          i === selectedVertexIndex
            ? previewPosition
            : { x: vertices[i].x, z: vertices[i].z };
        const j = (i + 1) % n;
        const posB =
          j === selectedVertexIndex
            ? previewPosition
            : { x: vertices[j].x, z: vertices[j].z };

        const isSnapped =
          (i === prevEdgeIdx && snapInfo.prevSnapped) ||
          (i === selectedEdgeIdx && snapInfo.nextSnapped);

        ctx.beginPath();
        ctx.moveTo(posA.x, posA.z);
        ctx.lineTo(posB.x, posB.z);
        if (isSnapped) {
          ctx.strokeStyle = SNAP_ENGAGED_COLOR;
          ctx.lineWidth = lineWidth * 2.5;
          ctx.setLineDash([]);
        } else {
          ctx.strokeStyle = MOVE_VERTEX_COLOR;
          ctx.lineWidth = lineWidth * 1.5;
          ctx.setLineDash([dashSize, dashSize]);
        }
        ctx.stroke();
      }
      ctx.setLineDash([]);
    }

    // Draw all non-selected vertices as blue dots
    for (let i = 0; i < vertices.length; i++) {
      if (i === selectedVertexIndex) continue;
      const v = vertices[i];
      ctx.beginPath();
      ctx.arc(v.x, v.z, VERTEX_RADIUS, 0, Math.PI * 2);
      ctx.fillStyle = VERTEX_COLOR;
      ctx.fill();
      ctx.strokeStyle = '#FFFFFF';
      ctx.lineWidth = lineWidth * 0.6;
      ctx.stroke();
    }

    // Draw ghost dot at original position of selected vertex
    const orig = vertices[selectedVertexIndex];
    ctx.beginPath();
    ctx.arc(orig.x, orig.z, VERTEX_RADIUS, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(33, 150, 243, 0.3)';
    ctx.fill();
    // Dashed ring around ghost
    ctx.beginPath();
    ctx.arc(orig.x, orig.z, MOVE_VERTEX_RADIUS * 1.5, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(33, 150, 243, 0.3)';
    ctx.lineWidth = lineWidth * 0.6;
    ctx.setLineDash([dashSize, dashSize]);
    ctx.stroke();
    ctx.setLineDash([]);

    // Draw selected vertex at preview position as amber dot
    ctx.beginPath();
    ctx.arc(
      previewPosition.x,
      previewPosition.z,
      MOVE_VERTEX_RADIUS,
      0,
      Math.PI * 2
    );
    ctx.fillStyle = MOVE_VERTEX_COLOR;
    ctx.fill();
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = lineWidth * 0.8;
    ctx.stroke();
  } else {
    // No vertex picked up — draw normal polygon with hover highlight

    // Draw polygon edges as thin blue lines
    ctx.strokeStyle = EDGE_COLOR;
    ctx.lineWidth = lineWidth;
    ctx.setLineDash([]);
    ctx.beginPath();
    ctx.moveTo(vertices[0].x, vertices[0].z);
    for (let i = 1; i < vertices.length; i++) {
      ctx.lineTo(vertices[i].x, vertices[i].z);
    }
    ctx.closePath();
    ctx.stroke();

    // Draw all vertices
    for (let i = 0; i < vertices.length; i++) {
      const v = vertices[i];
      const isHovered = i === hoveredVertexIndex;

      if (isHovered) {
        // Hovered vertex — amber circle with ring
        ctx.beginPath();
        ctx.arc(v.x, v.z, MOVE_VERTEX_RADIUS, 0, Math.PI * 2);
        ctx.fillStyle = MOVE_VERTEX_COLOR;
        ctx.fill();
        ctx.strokeStyle = '#FFFFFF';
        ctx.lineWidth = lineWidth * 0.8;
        ctx.stroke();

        // Outer amber ring
        ctx.beginPath();
        ctx.arc(v.x, v.z, MOVE_VERTEX_RADIUS * 2, 0, Math.PI * 2);
        ctx.strokeStyle = MOVE_VERTEX_COLOR;
        ctx.lineWidth = lineWidth * 0.6;
        ctx.stroke();
      } else {
        // Normal vertex — blue dot
        ctx.beginPath();
        ctx.arc(v.x, v.z, VERTEX_RADIUS, 0, Math.PI * 2);
        ctx.fillStyle = VERTEX_COLOR;
        ctx.fill();
        ctx.strokeStyle = '#FFFFFF';
        ctx.lineWidth = lineWidth * 0.6;
        ctx.stroke();
      }
    }
  }

  ctx.restore();
}
